/**
 * Camera math. The vertical FOV spans the **picture interior** (art §7.1–§7.2): the projection is off-centre so the
 * interior is the "lens" while the scene target still covers the whole canvas (bleed ring, frame-break). Includes the
 * engine's adapter of the art §7.2 arena solver (N28) until P2's `src/shared/cine/staging.ts` lands.
 */
import { m4LookAt, m4Mul, type CameraState, type Vec3 } from '../shared';

export interface Rect { x: number; y: number; w: number; h: number }

export interface CameraMatrices {
  view: Float32Array; proj: Float32Array; viewProj: Float32Array; eye: Float32Array;
}
export const newCameraMatrices = (): CameraMatrices => ({
  view: new Float32Array(16), proj: new Float32Array(16), viewProj: new Float32Array(16), eye: new Float32Array(3),
});

const UP: Vec3 = [0, 1, 0];
const tmpUp = new Float32Array(3);

/**
 * Builds view/proj/viewProj for `cam`, with the FOV mapped onto `picture` inside a canvas of `canvas` (same units).
 * `fovScale` < 1 = zoom punch (art §7.4: the only FOV change in gameplay). Zero allocation.
 */
export function cameraMatrices(out: CameraMatrices, cam: Readonly<CameraState>, canvas: { w: number; h: number }, picture: Rect, fovScale = 1): CameraMatrices {
  const fx = cam.target[0] - cam.eye[0], fy = cam.target[1] - cam.eye[1], fz = cam.target[2] - cam.eye[2];
  const fl = Math.hypot(fx, fy, fz) || 1;
  // dutch: roll the up vector about the forward axis
  const dr = (cam.dutchDeg * Math.PI) / 180;
  if (dr !== 0) {
    const nx = fx / fl, ny = fy / fl, nz = fz / fl;
    // right = f × up
    let rx = ny * UP[2] - nz * UP[1], ry = nz * UP[0] - nx * UP[2], rz = nx * UP[1] - ny * UP[0];
    const rl = Math.hypot(rx, ry, rz) || 1; rx /= rl; ry /= rl; rz /= rl;
    const ux = ry * nz - rz * ny, uy = rz * nx - rx * nz, uz = rx * ny - ry * nx;
    tmpUp[0] = ux * Math.cos(dr) + rx * Math.sin(dr); tmpUp[1] = uy * Math.cos(dr) + ry * Math.sin(dr); tmpUp[2] = uz * Math.cos(dr) + rz * Math.sin(dr);
  } else { tmpUp[0] = 0; tmpUp[1] = 1; tmpUp[2] = 0; }
  m4LookAt(out.view, cam.eye, cam.target, tmpUp);
  out.eye[0] = cam.eye[0]; out.eye[1] = cam.eye[1]; out.eye[2] = cam.eye[2];
  const n = cam.near, f = cam.far;
  const pcx = picture.x + picture.w / 2, pcy = picture.y + picture.h / 2;
  const halfH = cam.orthoHalfH > 0 ? cam.orthoHalfH * fovScale : n * Math.tan(((cam.fovYDeg * fovScale) * Math.PI) / 360);
  const scale = halfH / (picture.h / 2);
  const l = (0 - pcx) * scale, r = (canvas.w - pcx) * scale;
  const t = pcy * scale, b = (pcy - canvas.h) * scale;
  const p = out.proj;
  p.fill(0);
  if (cam.orthoHalfH > 0) {
    p[0] = 2 / (r - l); p[5] = 2 / (t - b); p[10] = 1 / (n - f); p[12] = -(r + l) / (r - l); p[13] = -(t + b) / (t - b); p[14] = n / (n - f); p[15] = 1;
  } else {
    p[0] = (2 * n) / (r - l); p[5] = (2 * n) / (t - b); p[8] = (r + l) / (r - l); p[9] = (t + b) / (t - b);
    p[10] = f / (n - f); p[11] = -1; p[14] = (n * f) / (n - f);
  }
  m4Mul(out.viewProj, out.proj, out.view);
  return out;
}

/** World → canvas units (same as `canvas`), plus visibility (in front of the camera, inside the canvas). */
export function projectPoint(vp: Float32Array, canvas: { w: number; h: number }, p: Readonly<Vec3>, out: { x: number; y: number; visible: boolean; depth: number }): typeof out {
  const x = vp[0] * p[0] + vp[4] * p[1] + vp[8] * p[2] + vp[12];
  const y = vp[1] * p[0] + vp[5] * p[1] + vp[9] * p[2] + vp[13];
  const z = vp[2] * p[0] + vp[6] * p[1] + vp[10] * p[2] + vp[14];
  const w = vp[3] * p[0] + vp[7] * p[1] + vp[11] * p[2] + vp[15];
  const iw = w !== 0 ? 1 / w : 0;
  out.x = (x * iw * 0.5 + 0.5) * canvas.w;
  out.y = (0.5 - y * iw * 0.5) * canvas.h;
  out.depth = z * iw;
  out.visible = w > 0 && out.x >= 0 && out.x <= canvas.w && out.y >= 0 && out.y <= canvas.h;
  return out;
}

// ── art §7.2 arena framing (engine adapter of the N28 solver) ───────────────────────────────────────────────────────

export interface ArenaFraming { cam: CameraState; hPx: number; hRestPx: number; hEngagedPx: number; groundPct: number; eyePct: number; layout: 'portrait' | 'landscape' }

/**
 * Art §7.2: H_rest = min(safeW / 1.46, (0.60 − g_min_rest)·interiorH / 0.84); H_engaged = min(1.15·H_rest,
 * safeW / (E + 0.46), (0.60 − g_min_eng)·interiorH / 0.84). FOV 20° / elevation 14° portrait, 18° / 12° on 16:10.
 * The eye line (0.84 H) lands at 60 % of the interior height. `picture` is the interior in CSS px; `s` the band scale.
 */
export function arenaFraming(picture: Rect, s: number, opts: { midX?: number; engagedE?: number | null; yawDeg?: number; scale?: number }): ArenaFraming {
  const landscape = picture.w / picture.h > 1.2;
  const fov = landscape ? 18 : 20;
  const elev = landscape ? 12 : 14;
  const gRest = landscape ? 0.15 : 0.12;
  const gEng = landscape ? 0.08 : 0.06;
  const safeW = picture.w - 12 * s;
  const hRest = Math.min(safeW / 1.46, ((0.6 - gRest) * picture.h) / 0.84);
  const E = opts.engagedE ?? null;
  const hEng = Math.min(1.15 * hRest, safeW / ((E ?? 0.55) + 0.46), ((0.6 - gEng) * picture.h) / 0.84);
  const hPx = E === null ? hRest : hEng;
  const k = opts.scale ?? 1;
  const tanHalf = Math.tan((fov * Math.PI) / 360);
  const dist = picture.h / (2 * tanHalf * hPx);
  const el = (elev * Math.PI) / 180;
  const yaw = ((opts.yawDeg ?? 0) * Math.PI) / 180;
  const midX = opts.midX ?? 0;
  // solve the aim height so the eye line (0.84 H) sits at 60 % of the interior from the bottom
  let aimY = 0.84 * k - (0.1 * picture.h) / (hPx * Math.cos(el));
  const cam: CameraState = { eye: [0, 0, 0], target: [midX, aimY, 0], fovYDeg: fov, dutchDeg: 0, orthoHalfH: 0, near: 0.1, far: 60 };
  const m = newCameraMatrices();
  const pr = { x: 0, y: 0, visible: false, depth: 0 };
  for (let it = 0; it < 4; it++) {
    cam.target[1] = aimY;
    cam.eye[0] = midX + Math.sin(yaw) * Math.cos(el) * dist;
    cam.eye[1] = aimY + Math.sin(el) * dist;
    cam.eye[2] = Math.cos(yaw) * Math.cos(el) * dist;
    cameraMatrices(m, cam, { w: picture.w, h: picture.h }, { x: 0, y: 0, w: picture.w, h: picture.h });
    projectPoint(m.viewProj, { w: picture.w, h: picture.h }, [midX, 0.84 * k, 0], pr);
    const err = (1 - pr.y / picture.h) - 0.6; // fraction above the target line
    aimY += (err * picture.h) / hPx;
  }
  projectPoint(m.viewProj, { w: picture.w, h: picture.h }, [midX, 0, 0], pr);
  const groundPct = 1 - pr.y / picture.h;
  return { cam, hPx, hRestPx: hRest, hEngagedPx: hEng, groundPct, eyePct: 0.6, layout: landscape ? 'landscape' : 'portrait' };
}

/** Lab / turntable camera: orbit around a target at a given FOV and elevation, fitting `heightH` into the interior. */
export function orbitCamera(target: Vec3, heightH: number, fovDeg: number, elevDeg: number, yawDeg: number): CameraState {
  const dist = (heightH * 0.5) / Math.tan((fovDeg * Math.PI) / 360) * 1.15;
  const el = (elevDeg * Math.PI) / 180, yaw = (yawDeg * Math.PI) / 180;
  return {
    eye: [target[0] + Math.sin(yaw) * Math.cos(el) * dist, target[1] + Math.sin(el) * dist, target[2] + Math.cos(yaw) * Math.cos(el) * dist],
    target: [target[0], target[1], target[2]], fovYDeg: fovDeg, dutchDeg: 0, orthoHalfH: 0, near: 0.1, far: 80,
  };
}
